import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  message,
  theme,
  Space,
  Modal,
  Table,
  Tag,
  Select,
  Alert
} from 'antd';
import { PlusOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { HiDocumentText } from 'react-icons/hi';
import {
  listForms,
  deleteForm,
  loadFormDto,
  exportFormsJson,
  previewFormsImport,
  applyFormsImport
} from '../../actions/formActions';
import ListItemsDataTable from '../../components/crud/ListItemsDataTable.component';
import PageWrapper from '../../components/common/PageWrapper';
import useDocumentTitle from '../../utils/useDocumentTitle';
import { useTranslation } from 'react-i18next';
import ApiErrorAlert from '../../components/common/ApiErrorAlert';

const FormsListScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { token } = theme.useToken();
  useDocumentTitle('Dynamic Forms');
  
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    filter: 'Name',
    query: '',
    sortBy: 'Name',
    sortOrder: 'ASC'
  });
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importing, setImporting] = useState(false);

  // Redux state variables
  const formList = useSelector((state) => state.formList);
  const { loading: loadingList, error: errorList, forms, currentPage, totalPages, totalCount } = formList;

  const formDelete = useSelector((state) => state.formDelete);
  const { success: deleteSuccess, error: deleteError } = formDelete;

  const formDto = useSelector((state) => state.formDto);
  const { formDto: dtoConfig, loading: loadingDto, error: errorDto } = formDto;

  // Memoize dtoConfig check to prevent unnecessary re-renders
  const hasDtoConfig = useMemo(() => {
    return dtoConfig && Object.keys(dtoConfig).length > 0;
  }, [dtoConfig]);

  // Fetch DTO structure for table columns - only once
  useEffect(() => {
    if (!hasDtoConfig && !loadingDto) {
      dispatch(loadFormDto());
    }
  }, [dispatch, hasDtoConfig, loadingDto]);

  // Load forms when filter changes
  useEffect(() => {
    dispatch(listForms(filter));
  }, [dispatch, filter.page, filter.limit, filter.filter, filter.query, filter.sortBy, filter.sortOrder]);

  // Reload forms after successful delete; surface a clear message if delete was blocked (e.g. form in use)
  useEffect(() => {
    if (deleteSuccess) {
      message.success(t('Form deleted successfully'));
      dispatch(listForms(filter));
      dispatch({ type: 'FORM_DELETE_RESET' });
    }
    if (deleteError) {
      message.error(Array.isArray(deleteError) ? deleteError.join(', ') : deleteError, 6);
      dispatch({ type: 'FORM_DELETE_RESET' });
    }
  }, [dispatch, deleteSuccess, deleteError, filter, t]);

  const handleDelete = useCallback((id) => {
    dispatch(deleteForm(id));
  }, [dispatch]);

  const handleEditForm = useCallback((id) => {
    navigate(`/settings/forms/edit/${id}`);
  }, [navigate]);

  const handleFilterChange = useCallback((newFilter) => {
    setFilter(prev => ({ ...prev, ...newFilter }));
  }, []);

  const handleRetry = useCallback(() => {
    dispatch(listForms(filter));
    dispatch(loadFormDto());
  }, [dispatch, filter]);

  const downloadJsonFile = (filename, payload) => {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const normalizeFormForWrite = (form) => ({
    code: form.code,
    name: form.name,
    description: form.description || '',
    schemaJson: typeof form.schemaJson === 'string' ? form.schemaJson : JSON.stringify(form.schemaJson || {}),
    version: form.version || '1.0.0',
    entityType: form.entityType || null,
    entityId: form.entityId || null,
    isActive: form.isActive ?? true,
    isPublished: form.isPublished ?? false,
    autoSave: form.autoSave ?? true
  });

  const handleExportForms = async () => {
    try {
      const payload = await dispatch(exportFormsJson());
      downloadJsonFile(`forms-export-${new Date().toISOString().slice(0, 10)}.json`, payload);
      message.success(`Exported ${payload?.total || 0} forms`);
    } catch (err) {
      message.error(err?.response?.data?.detail || err?.message || 'Failed to export forms');
    }
  };

  const openImportPreview = async (importedForms, { overrideExisting = false } = {}) => {
    if (!Array.isArray(importedForms) || importedForms.length === 0) {
      message.error('No forms found to import');
      return;
    }

    const normalizedForms = importedForms
      .filter((f) => f?.code && f?.name)
      .map((f) => normalizeFormForWrite(f));

    const preview = await dispatch(previewFormsImport(normalizedForms));
    const rows = (preview?.items || []).map((item, idx) => ({
      key: `${item.code}-${idx}`,
      imported: item.form,
      existingId: item.existingId || null,
      exists: Boolean(item.exists),
      action: item.exists
        ? (overrideExisting ? 'override' : (item.action || 'skip'))
        : 'create'
    }));

    if (!rows.length) {
      message.error('No valid forms (with code + name) found');
      return;
    }

    setImportRows(rows);
    setImportModalOpen(true);
  };

  const handleImportFilePicked = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const importedForms = Array.isArray(parsed) ? parsed : parsed?.forms;
      await openImportPreview(importedForms, { overrideExisting: false });
    } catch (err) {
      message.error(err?.message || 'Invalid JSON file');
    }
  };

  const updateRowAction = (rowKey, action) => {
    setImportRows((prev) => prev.map((r) => (r.key === rowKey ? { ...r, action } : r)));
  };

  const handleRunImport = async () => {
    try {
      setImporting(true);
      let created = 0;
      let overridden = 0;
      let skipped = 0;

      for (const row of importRows) {
        if (row.action === 'skip') skipped += 1;
        else if (row.action === 'override' && row.existingId) overridden += 1;
        else if (row.action === 'create') created += 1;
      }

      const payload = importRows.map((row) => ({
        form: row.imported,
        action: row.action,
        existingId: row.existingId
      }));
      const result = await dispatch(applyFormsImport(payload));
      created = result?.created ?? created;
      overridden = result?.overridden ?? overridden;
      skipped = result?.skipped ?? skipped;

      setImportModalOpen(false);
      setImportRows([]);
      dispatch(listForms(filter));
      message.success(`Import complete. Created: ${created}, Overridden: ${overridden}, Skipped: ${skipped}`);
    } catch (err) {
      message.error(err?.response?.data?.detail || err?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const importColumns = [
    { title: 'Code', dataIndex: ['imported', 'code'], key: 'code' },
    { title: 'Name', dataIndex: ['imported', 'name'], key: 'name' },
    {
      title: 'Match',
      key: 'match',
      render: (_, row) =>
        row.exists ? <Tag color="orange">Existing</Tag> : <Tag color="green">New</Tag>
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, row) => (
        <Select
          value={row.action}
          style={{ width: 140 }}
          onChange={(value) => updateRowAction(row.key, value)}
          options={
            row.exists
              ? [
                  { value: 'skip', label: 'Skip' },
                  { value: 'override', label: 'Override' }
                ]
              : [{ value: 'create', label: 'Create' }]
          }
        />
      )
    }
  ];

  const headerActions = (
    <Space>
      <Button icon={<DownloadOutlined />} onClick={handleExportForms}>
        Export JSON
      </Button>
      <Button icon={<UploadOutlined />} onClick={() => document.getElementById('clinical-forms-import-input')?.click()}>
        Import JSON
      </Button>
      <input
        id="clinical-forms-import-input"
        type="file"
        accept="application/json"
        style={{ display: 'none' }}
        onChange={handleImportFilePicked}
      />
      <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/settings/forms/create')}>
        Create Form
      </Button>
    </Space>
  );

  return (
    <PageWrapper
      title="Dynamic Forms"
      subtitle="Manage schema-driven clinical assessment forms"
      icon={<HiDocumentText style={{ fontSize: 24, color: token.colorPrimary }} />}
      headerActions={headerActions}
    >
      {hasDtoConfig ? (
        <ListItemsDataTable
          dtoConfig={dtoConfig}
          data={forms || []}
          filter={filter}
          onFilterChange={handleFilterChange}
          currentPage={filter.page}
          totalPages={totalPages || 1}
          totalCount={totalCount || 0}
          limit={filter.limit}
          loading={loadingList || loadingDto}
          handleDeleteRow={handleDelete}
          handleEditForm={handleEditForm}
          error={errorList || errorDto}
          onRetry={handleRetry}
        />
      ) : (
        <>
          {(errorList || errorDto) ? (
            <ApiErrorAlert
              error={errorList || errorDto}
              closable
              action={
                <Button size="small" onClick={handleRetry}>
                  {t('Retry')}
                </Button>
              }
            />
          ) : (
            <div style={{ textAlign: 'center', padding: 40 }}>{t('Loading Data ...')}</div>
          )}
        </>
      )}

      <Modal
        open={importModalOpen}
        title="Import Forms (Selective Override)"
        onCancel={() => !importing && setImportModalOpen(false)}
        onOk={handleRunImport}
        okButtonProps={{ loading: importing }}
        width={900}
        okText="Run Import"
      >
        <Table dataSource={importRows} columns={importColumns} pagination={{ pageSize: 8 }} size="small" />
      </Modal>
    </PageWrapper>
  );
};

export default FormsListScreen;

